import { useEffect, useState } from "react";
import { api, mediaUrl } from "../lib/api";
import { LinkedinLogo, ArrowSquareOut } from "@phosphor-icons/react";

const LINKEDIN_URL = "https://www.linkedin.com/company/wisewit-solutions/";

/**
 * One LinkedIn card with a self-healing image loader — if the admin's
 * uploaded cover_image URL 404s or errors, we seamlessly swap to a
 * LinkedIn-logo placeholder so the homepage never shows a broken image.
 */
function LinkedInCard({ post }) {
  const [imgOk, setImgOk] = useState(!!post.cover_image);
  const src = post.cover_image ? mediaUrl(post.cover_image) : "";
  return imgOk ? (
    <div className="aspect-[16/10] overflow-hidden border border-[#2A2A2A] mb-4 bg-[#0a0a0a]">
      <img
        src={src}
        alt={post.title}
        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
        onError={() => setImgOk(false)}
      />
    </div>
  ) : (
    <div className="aspect-[16/10] border border-[#2A2A2A] mb-4 bg-gradient-to-br from-[#0A66C2]/20 to-[#0a0a0a] flex items-center justify-center">
      <LinkedinLogo size={48} weight="fill" color="#0A66C2" />
    </div>
  );
}

/**
 * Homepage section that surfaces Wisewit's LinkedIn posts. Any article the
 * admin adds via Knowledge with `external_url` set + `source = "LinkedIn"`
 * shows up here automatically. If none are configured yet, we still show
 * the "Follow us on LinkedIn" CTA so the section is never empty.
 */
export default function FromLinkedIn() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/articles").then(({ data }) => {
      const linkedin = (data || []).filter(
        (a) => a.external_url && (a.source || "").toLowerCase() === "linkedin"
      );
      setPosts(linkedin.slice(0, 6));
    }).catch(() => {});
  }, []);

  return (
    <section
      data-testid="from-linkedin-section"
      className="relative px-6 md:px-14 py-24 border-b border-[#2A2A2A]"
    >
      <div className="max-w-6xl flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-overline mb-4 flex items-center gap-2">
            <LinkedinLogo size={14} weight="fill" color="#0A66C2" /> From LinkedIn
          </div>
          <h2 className="font-display font-semibold tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]">
            Recent <span className="text-[#FF4500]">posts</span> from the team.
          </h2>
        </div>
        <a
          href={LINKEDIN_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="linkedin-follow-cta"
          className="btn-secondary inline-flex items-center gap-2"
        >
          <LinkedinLogo size={16} weight="fill" /> Follow @wisewit-solutions
        </a>
      </div>

      {posts.length === 0 ? (
        <div className="mt-10 ww-card p-8 max-w-3xl">
          <p className="text-[#8E8E93] text-sm leading-relaxed">
            No LinkedIn posts wired up yet.
            Follow the button above, or from <code className="text-[#DFFF00]">/admin → Knowledge → New</code>{" "}
            add a title, paste the LinkedIn URL as <em>External link</em>, and pick <em>Source: LinkedIn</em>. It will appear here.
          </p>
        </div>
      ) : (
        <div
          className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]"
          data-testid="linkedin-posts-grid"
        >
          {posts.map((p) => (
            <a
              key={p.id}
              href={p.external_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`linkedin-post-${p.slug}`}
              className="bg-[#050505] p-6 hover:bg-[#0a0a0a] transition-colors group ww-trace"
            >
              <LinkedInCard post={p} />
              <div className="flex items-center gap-2">
                <LinkedinLogo size={12} weight="fill" color="#0A66C2" />
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#0A66C2]">LinkedIn</span>
                <span className="ml-auto font-mono text-[10px] text-[#8E8E93]">{p.category}</span>
              </div>
              <h3 className="mt-3 font-display text-xl text-white leading-tight">{p.title}</h3>
              <p className="mt-2 text-sm text-[#8E8E93] leading-relaxed line-clamp-3">{p.summary}</p>
              <div className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[#FF4500] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                Open on LinkedIn <ArrowSquareOut size={11} />
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
